/**
 * Renderer-neutral frame data: what the frames store holds per frame and what
 * every frame component reads. Canvas geometry lives in the canvas store.
 */
import type { FrameSummary } from "@/api/frame-listing";
import type { Frame, FrameStatus, FrameType } from "@/api/frame-model";

/** `"card"`: loaded from a summary, `content` is empty until detail loads. */
export type FrameContentState = "full" | "card";

export interface FrameTaskTicket {
  id: string;
  number: number;
  title: string;
}

/** `extends Record<string, unknown>`: React Flow's `Node<T>` constraint. */
export interface FrameData extends Record<string, unknown> {
  frameId: string;
  spaceId?: string;
  frameType: FrameType;
  title: string;
  content: Record<string, unknown>;
  status: FrameStatus;
  agentId: string | null;
  parentFrame: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt?: string;
  taskTicket: FrameTaskTicket | null;
  editedByUserAt: string | null;
  /** Set while archived (off the wall); absent means not archived. */
  archivedAt?: string | null;
  /** Server-computed bounded projection of `content`, same schema. */
  card?: Record<string, unknown> | null;
  /** The `contentVersion` the card was received with; stale when lower. */
  cardVersion?: number | null;
  /** Increments on every content change. Absent means 0. */
  contentVersion?: number;
  /** Absent means `"full"`. */
  contentState?: FrameContentState;
}

/** Fields a server payload or a local edit may change. */
export type FrameDataPatch = Partial<
  Pick<
    FrameData,
    | "title"
    | "content"
    | "status"
    | "metadata"
    | "taskTicket"
    | "editedByUserAt"
    | "archivedAt"
    | "updatedAt"
    | "card"
  >
>;

/** Frame types that have a registered component; retired types stay stored. */
export const REGISTERED_FRAME_TYPES: ReadonlySet<string> = new Set([
  "markdown",
  "html",
  "code",
  "search",
  "image",
  "email",
  "connection",
  "connection_prompt",
  "memory",
  "note",
  "doc",
  "conversation",
  "taskboard",
  "github",
  "event",
  "agenda",
  "flight",
  "weather",
  "chart",
  "dataset",
  "preview_card",
  "link_preview",
  "chat",
  "slack",
  "todo",
  "board",
  "music",
  "calendar",
  "file",
  "terminal",
]);

function taskTicketFromApi(
  frame: Pick<Frame, "task_id" | "task_ticket_number" | "task_title">,
): FrameTaskTicket | null {
  return frame.task_id && frame.task_ticket_number != null
    ? {
        id: frame.task_id,
        number: frame.task_ticket_number,
        title: frame.task_title ?? "",
      }
    : null;
}

function sameTaskTicket(
  left: FrameTaskTicket | null,
  right: FrameTaskTicket | null,
): boolean {
  if (left === right) return true;
  return (
    !!left &&
    !!right &&
    left.id === right.id &&
    left.number === right.number &&
    left.title === right.title
  );
}

export function frameDataFromApi(frame: Frame): FrameData {
  return {
    frameId: frame.id,
    spaceId: frame.project ?? frame.space,
    frameType: frame.frame_type,
    title: frame.title,
    content: frame.content,
    status: frame.status,
    agentId: frame.agent_id,
    parentFrame: frame.parent_frame ?? null,
    metadata: frame.metadata,
    createdAt: frame.created_at,
    updatedAt: frame.updated_at,
    taskTicket: taskTicketFromApi(frame),
    editedByUserAt: frame.edited_by_user_at ?? null,
    archivedAt: frame.archived_at ?? null,
    card: frame.card ?? null,
    cardVersion: frame.card !== undefined ? 0 : null,
    contentVersion: 0,
    contentState: "full",
  };
}

/** The data half of an API frame patch (geometry is the canvas store's). */
export function frameDataPatchFromApi(patch: Partial<Frame>): FrameDataPatch {
  const data: FrameDataPatch = {};
  if (patch.title !== undefined) data.title = patch.title;
  if (patch.content !== undefined) data.content = patch.content;
  if (patch.status !== undefined) data.status = patch.status;
  if (patch.metadata !== undefined) data.metadata = patch.metadata;
  if (patch.task_id !== undefined) {
    data.taskTicket = taskTicketFromApi({
      task_id: patch.task_id,
      task_ticket_number: patch.task_ticket_number ?? null,
      task_title: patch.task_title ?? null,
    });
  }
  if (patch.edited_by_user_at !== undefined) {
    data.editedByUserAt = patch.edited_by_user_at ?? null;
  }
  if (patch.archived_at !== undefined) data.archivedAt = patch.archived_at;
  if (patch.updated_at !== undefined) data.updatedAt = patch.updated_at;
  if (patch.card !== undefined) data.card = patch.card;
  return data;
}

/** True when `candidate` is a later server time than `current` (or `current` is unknown). */
export function isNewerServerTime(
  candidate: string,
  current: string | undefined,
): boolean {
  return !current || Date.parse(candidate) > Date.parse(current);
}

/**
 * A frame known only from the summary listing: card, no content. Its project
 * comes from the summary, else from `fallbackProjectId`.
 */
export function frameDataFromSummary(
  summary: FrameSummary,
  fallbackProjectId?: string,
): FrameData {
  return {
    frameId: summary.id,
    spaceId: summary.project_id ?? fallbackProjectId,
    frameType: summary.frame_type,
    title: summary.title,
    content: {},
    status: summary.status,
    agentId: summary.agent_id,
    parentFrame: summary.parent_frame,
    metadata: summary.metadata,
    createdAt: summary.created_at,
    updatedAt: summary.updated_at,
    taskTicket: taskTicketFromApi(summary),
    editedByUserAt: summary.edited_by_user_at,
    archivedAt: summary.archived_at,
    card: summary.card,
    cardVersion: 0,
    contentVersion: 0,
    contentState: "card",
  };
}

const PLAIN_PATCH_KEYS = [
  "title",
  "status",
  "metadata",
  "editedByUserAt",
  "archivedAt",
  "updatedAt",
] as const;

/**
 * Apply a patch, returning `previous` itself when nothing changed so stores
 * and canvas nodes keep their identity. New content bumps `contentVersion`
 * and makes the frame `"full"`; a card is stamped with the resulting version.
 */
export function mergeFrameData(
  previous: FrameData,
  patch: FrameDataPatch,
): FrameData {
  let next: FrameData | null = null;
  const draft = (): FrameData => (next ??= { ...previous });
  for (const key of PLAIN_PATCH_KEYS) {
    const value = patch[key];
    if (value !== undefined && !Object.is(previous[key], value)) {
      (draft() as Record<string, unknown>)[key] = value;
    }
  }
  if (
    patch.taskTicket !== undefined &&
    !sameTaskTicket(previous.taskTicket, patch.taskTicket)
  ) {
    draft().taskTicket = patch.taskTicket;
  }
  if (patch.content !== undefined && patch.content !== previous.content) {
    const frame = draft();
    frame.content = patch.content;
    frame.contentVersion = (previous.contentVersion ?? 0) + 1;
    frame.contentState = "full";
  }
  if (patch.card !== undefined) {
    const contentVersion =
      (next as FrameData | null)?.contentVersion ??
      previous.contentVersion ??
      0;
    if (
      patch.card !== previous.card ||
      previous.cardVersion !== contentVersion
    ) {
      const frame = draft();
      frame.card = patch.card;
      frame.cardVersion = contentVersion;
    }
  }
  return next ?? previous;
}
